/* ---------------------------------------------------------------------------
   DECODE BIOME PDP — behaviour
   ---------------------------------------------------------------------------
   Everything here is presentation only: plan switching, panel lock states,
   live price/label sync, quantity stepping, the description clamp toggle,
   the discount-code copy button and the info-grid popups.

   Add to cart deliberately contains NO custom logic. The section renders a
   stock `<form is="product-form">`, so adding to cart, cart-drawer refresh
   and error handling are all handled by the theme exactly as they are for
   every other product. Switching plan only rewrites the value of the form's
   hidden `name="id"` input, which is the one thing Shopify needs to add the
   right variant.
   --------------------------------------------------------------------------- */

// ---------------------------------------------------------------------------
// Quantity selector — matches snippets/biome-kit-quantity-selector.liquid
// ---------------------------------------------------------------------------
class BiomeKitQuantitySelector extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this.input = this.querySelector('[data-biome-kit-qty-input]');
    this.decreaseBtn = this.querySelector('[data-biome-kit-qty-decrease]');
    this.increaseBtn = this.querySelector('[data-biome-kit-qty-increase]');

    this.decreaseBtn?.addEventListener('click', () => this._step(-1), { signal });
    this.increaseBtn?.addEventListener('click', () => this._step(1), { signal });
    this.input?.addEventListener('change', () => this._commit(this.input.value), { signal });

    this._updateButtonStates();
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  get min() {
    return parseInt(this.getAttribute('data-min') || '1', 10);
  }

  get max() {
    return this.hasAttribute('data-max') ? parseInt(this.getAttribute('data-max'), 10) : Infinity;
  }

  get step() {
    return parseInt(this.getAttribute('data-step') || '1', 10);
  }

  get quantity() {
    return parseInt(this.input?.value || this.min, 10);
  }

  _step(direction) {
    this._commit(this.quantity + direction * this.step);
  }

  _commit(rawValue) {
    let value = parseInt(rawValue, 10);
    if (isNaN(value)) value = this.min;

    // Snap to the nearest valid step above the minimum, then clamp.
    const stepsFromMin = Math.round((value - this.min) / this.step);
    value = this.min + stepsFromMin * this.step;
    value = Math.max(this.min, Math.min(value, this.max));

    const changed = this.input.value !== String(value);
    this.input.value = value;
    this._updateButtonStates();

    if (changed) {
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _updateButtonStates() {
    if (this.decreaseBtn) this.decreaseBtn.disabled = this.quantity <= this.min;
    if (this.increaseBtn) this.increaseBtn.disabled = this.quantity >= this.max;
  }
}

if (!window.customElements.get('biome-kit-quantity-selector')) {
  window.customElements.define('biome-kit-quantity-selector', BiomeKitQuantitySelector);
}

// ---------------------------------------------------------------------------
// Plan selector + panels + price/label sync
// ---------------------------------------------------------------------------
(function () {
  if (window.__biomeKitPdpInit) return;
  window.__biomeKitPdpInit = true;

  const stripTrailingZeros = (money) => money.replace(/([.,])0{2}(?=\D*$)/, '');

  function moneyFormatter(section) {
    const format =
      section.dataset.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat ||
      window.Shopify?.money_format;

    return (cents) => {
      if (window.Shopify?.formatMoney) {
        return stripTrailingZeros(window.Shopify.formatMoney(cents, format));
      }

      const currency = window.Shopify?.currency?.active || 'INR';
      try {
        return stripTrailingZeros(
          new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
        );
      } catch {
        return `${currency} ${(cents / 100).toFixed(2)}`;
      }
    };
  }

  function initSection(section) {
    const planInputs = Array.from(section.querySelectorAll('[data-bk-plan-input]'));
    const quantitySelector = section.querySelector('biome-kit-quantity-selector');
    const variantInput = section.querySelector('[data-bk-variant-input]');
    const formatMoney = moneyFormatter(section);

    // Elements that reflect the current selection. All optional — a merchant can
    // remove any block from the section and the rest keeps working.
    const els = {
      priceCurrent: section.querySelector('[data-bk-price-current]'),
      priceCompare: section.querySelector('[data-bk-price-compare]'),
      priceSaving: section.querySelector('[data-bk-price-saving]'),
      saveBadge: section.querySelector('[data-bk-save-badge]'),
      panelsCount: section.querySelector('[data-bk-panels-count]'),
      atcLabel: section.querySelector('[data-bk-atc-label]'),
      atcLabelDesktop: section.querySelector('[data-bk-atc-label-desktop]'),
      atcSub: section.querySelector('[data-bk-atc-sub]'),
      atcTotal: section.querySelector('[data-bk-atc-total]'),
      atcButton: section.querySelector('[data-bk-atc-button]'),
    };

    const panels = Array.from(section.querySelectorAll('[data-bk-panel]'));

    // Stand-in for a plan radio, built from the state the section renders onto itself. Used when the
    // plan cards aren't on the page — with a single buyable plan there are no radios to read, but the
    // running total, button label and panel lock states still have to be right.
    const fallbackPlan = {
      dataset: {
        tier: section.dataset.initialTier,
        name: section.dataset.initialName,
        panelCount: section.dataset.initialPanelCount,
        price: section.dataset.initialPrice,
        compare: section.dataset.initialCompare,
        available: section.dataset.initialAvailable,
        variantId: section.dataset.initialVariantId,
      },
    };

    function selectedPlan() {
      return planInputs.find((input) => input.checked) || planInputs[0] || fallbackPlan;
    }

    function currentQuantity() {
      return quantitySelector ? quantitySelector.quantity : 1;
    }

    function render() {
      const plan = selectedPlan();
      if (!plan) return;

      const tier = Number(plan.dataset.tier) || 1;
      const price = parseInt(plan.dataset.price, 10) || 0;
      const compareAt = parseInt(plan.dataset.compare, 10) || 0;
      const available = plan.dataset.available === 'true';
      const planName = plan.dataset.name || '';
      const qty = currentQuantity();

      // Add to cart targets this plan's variant. This is the only cart-facing change.
      if (variantInput && plan.dataset.variantId) {
        variantInput.value = plan.dataset.variantId;
      }

      // --- price row -------------------------------------------------------
      if (els.priceCurrent) els.priceCurrent.textContent = formatMoney(price);

      if (els.priceCompare) {
        els.priceCompare.textContent = compareAt > price ? formatMoney(compareAt) : '';
      }

      if (els.priceSaving) {
        if (compareAt > price && compareAt > 0) {
          const percent = Math.round(((compareAt - price) / compareAt) * 100);
          els.priceSaving.textContent = `${percent}% off`;
        } else {
          els.priceSaving.textContent = '';
        }
      }

      if (els.saveBadge) {
        const saving = compareAt - price;
        if (saving > 0) {
          const template = els.saveBadge.dataset.template || 'Save [amount]';
          els.saveBadge.textContent = template.replace('[amount]', formatMoney(saving));
          els.saveBadge.hidden = false;
        } else {
          els.saveBadge.hidden = true;
        }
      }

      // --- panels ----------------------------------------------------------
      panels.forEach((panel) => {
        const panelTier = Number(panel.dataset.tier) || 1;
        panel.classList.toggle('is-locked', panelTier > tier);
      });

      if (els.panelsCount) {
        els.panelsCount.textContent = plan.dataset.panelCount || '';
      }

      // --- add to cart -----------------------------------------------------
      // Two label elements (mobile / desktop) are kept in sync on every render regardless of which
      // one CSS is currently showing, so a breakpoint change mid-session (e.g. rotating a tablet)
      // never leaves the visible one showing stale text.
      if (els.atcLabel) {
        const template = els.atcLabel.dataset.template || 'Add to cart';
        els.atcLabel.textContent = available
          ? template.replace('[plan]', planName)
          : els.atcLabel.dataset.soldOut || 'Sold out';
      }

      if (els.atcLabelDesktop) {
        const template = els.atcLabelDesktop.dataset.template || 'Add to cart';
        els.atcLabelDesktop.textContent = available
          ? template.replace('[plan]', planName)
          : els.atcLabelDesktop.dataset.soldOut || 'Sold out';
      }

      const total = price * qty;

      if (els.atcTotal) els.atcTotal.textContent = formatMoney(total);

      if (els.atcSub) {
        const template = els.atcSub.dataset.template || '';
        els.atcSub.textContent = template ? template.replace('[price]', formatMoney(total)) : '';
      }

      if (els.atcButton) els.atcButton.disabled = !available;
    }

    planInputs.forEach((input) => {
      input.addEventListener('change', render);
    });

    quantitySelector
      ?.querySelector('[data-biome-kit-qty-input]')
      ?.addEventListener('change', render);

    render();
  }

  function initAll() {
    document.querySelectorAll('[data-bk-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The section re-renders itself on variant/cart changes (product-rerender),
  // so re-bind against whatever markup is on the page afterwards.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-bk-section]').forEach(initSection);
  });
})();

// ---------------------------------------------------------------------------
// Description clamp toggle
// ---------------------------------------------------------------------------
(function () {
  if (window.__biomeKitDescriptionInit) return;
  window.__biomeKitDescriptionInit = true;

  function initDescriptionToggles() {
    document.querySelectorAll('[data-biome-kit-description]').forEach((wrapper) => {
      const content = wrapper.querySelector('[data-biome-kit-description-content]');
      const toggle = wrapper.querySelector('[data-biome-kit-description-toggle]');
      if (!content || !toggle) return;

      // Only offer "Read more" when the text is actually clamped, so short
      // descriptions never get a pointless toggle.
      toggle.hidden = content.scrollHeight <= content.clientHeight + 2;
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-biome-kit-description-toggle]');
    if (!toggle) return;

    const wrapper = toggle.closest('[data-biome-kit-description]');
    const label = toggle.querySelector('[data-biome-kit-description-toggle-label]');
    const isExpanded = wrapper.classList.toggle('is-expanded');

    if (label) {
      label.textContent = isExpanded
        ? toggle.dataset.labelLess || 'Read less'
        : toggle.dataset.labelMore || 'Read more';
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescriptionToggles);
  } else {
    initDescriptionToggles();
  }
})();

// ---------------------------------------------------------------------------
// Discount code copy button
// ---------------------------------------------------------------------------
(function () {
  if (window.__biomeKitCopyCodeInit) return;
  window.__biomeKitCopyCodeInit = true;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-biome-kit-copy-code]');
    if (!btn) return;

    // Ignore repeat clicks while the "Copied" state is still showing.
    if (btn.dataset.biomeKitCopying === 'true') return;

    const code = btn.getAttribute('data-biome-kit-copy-code');
    const codeText = btn.querySelector('[data-biome-kit-code-text]');
    const copiedText = btn.querySelector('[data-biome-kit-code-copied]');

    navigator.clipboard?.writeText(code).then(() => {
      btn.dataset.biomeKitCopying = 'true';

      if (codeText) codeText.style.display = 'none';
      if (copiedText) copiedText.style.display = 'inline';

      setTimeout(() => {
        if (codeText) codeText.style.display = '';
        if (copiedText) copiedText.style.display = 'none';
        btn.dataset.biomeKitCopying = 'false';
      }, 2000);
    });
  });
})();

// ---------------------------------------------------------------------------
// Info-grid popups
// ---------------------------------------------------------------------------
(function () {
  if (window.__biomeKitInfoPopupInit) return;
  window.__biomeKitInfoPopupInit = true;

  function closeAllPopups() {
    document.querySelectorAll('[data-biome-kit-popup]').forEach((popup) => {
      popup.classList.remove('is-open');
      // Wait for the fade-out before removing it from the layout.
      setTimeout(() => {
        if (!popup.classList.contains('is-open')) popup.hidden = true;
      }, 200);
    });
    document.body.classList.remove('biome-kit-popup-open');
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-biome-kit-popup-trigger]');

    if (trigger) {
      const targetId = trigger.getAttribute('data-biome-kit-popup-trigger');
      const popup = document.querySelector(`[data-biome-kit-popup="${targetId}"]`);
      if (!popup) return;

      // Drop `hidden` first and add `is-open` on a later frame — doing both in
      // one tick skips the transition, because the browser never painted the
      // closed state.
      popup.hidden = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => popup.classList.add('is-open'));
      });
      document.body.classList.add('biome-kit-popup-open');
      return;
    }

    if (event.target.closest('[data-biome-kit-popup-close]')) {
      closeAllPopups();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllPopups();
  });
})();

// ---------------------------------------------------------------------------
// Mobile bottom-bar swap on scroll direction
// Only one fixed bottom bar shows at a time: our add-to-cart bar while
// scrolling down, the theme's own mobile nav (.mobile_stickey_menu) while
// scrolling up. See the CSS comment in sections/main-product-biome.liquid for
// why the theme's own buy-now-bar script can't drive this on this section.
// ---------------------------------------------------------------------------
(function () {
  if (window.__biomeKitStickyBarInit) return;
  window.__biomeKitStickyBarInit = true;

  const atcBar = document.querySelector('.biome-kit__atc');
  const menuBar = document.querySelector('.mobile_stickey_menu');
  if (!atcBar || !menuBar) return;

  const mobileQuery = window.matchMedia('(max-width: 999px)');
  let lastScrollY = window.scrollY;
  let ticking = false;

  function showAtcBar() {
    atcBar.classList.remove('biome-kit__atc-hidden');
    menuBar.classList.remove('is-visible');
  }

  function showMenuBar() {
    atcBar.classList.add('biome-kit__atc-hidden');
    menuBar.classList.add('is-visible');
  }

  function handleScroll() {
    ticking = false;

    if (!mobileQuery.matches) {
      showAtcBar();
      lastScrollY = window.scrollY;
      return;
    }

    const currentScrollY = window.scrollY;

    if (currentScrollY <= 0) {
      showAtcBar();
    } else if (currentScrollY > lastScrollY) {
      showAtcBar();
    } else if (currentScrollY < lastScrollY) {
      showMenuBar();
    }

    lastScrollY = currentScrollY;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(handleScroll);
    },
    { passive: true }
  );

  mobileQuery.addEventListener('change', handleScroll);
})();
